import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

export default class extends Controller {
  static values = {
    fields: Array,
    documents: Array,
    signerColor: String,
    signerName: String,
    token: String,
    csrfToken: String,
    locale: String,
    signatureFonts: Object
  }

  static targets = [
    "pdfContainer",
    "fieldOverlay",
    "progressBar",
    "progressText",
    "fieldList",
    "fieldListItem",
    "previousBtn",
    "nextBtn",
    "submitBtn",
    "documentIndicator",
    "documentSection",
    "documentBadge",
    "signatureModal",
    "signatureInput",
    "signaturePreview",
    "fontOptions",
    "fontSample",
    "initialsModal",
    "initialsInput",
    "initialsPreview",
    "initialsFontOptions"
  ]

  connect() {
    this.currentPage = 1
    this.currentDocumentIndex = 0
    this.currentFieldIndex = 0
    this.completedFields = new Map()
    this.selectedSignatureFont = "rock_salt"
    this.selectedInitialsFont = "rock_salt"
    this.currentFieldId = null
    this.submitting = false

    // Initialize from server data
    this.initializeFieldStates()

    // Listen for page changes from pdf-viewer controller
    this.element.addEventListener("pdf-viewer:pageRendered", this.handlePageRendered.bind(this))

    // Initialize Bootstrap modals
    if (this.hasSignatureModalTarget) {
      this.signatureModalInstance = new bootstrap.Modal(this.signatureModalTarget)
    }
    if (this.hasInitialsModalTarget) {
      this.initialsModalInstance = new bootstrap.Modal(this.initialsModalTarget)
    }
  }

  disconnect() {
    this.element.removeEventListener("pdf-viewer:pageRendered", this.handlePageRendered.bind(this))
  }

  initializeFieldStates() {
    // Load completed fields from server data
    this.fieldsValue.forEach(field => {
      if (field.completed) {
        this.completedFields.set(field.id, {
          value_text: field.value_text,
          value_meta: field.value_meta,
          font_class: field.font_class
        })
      }
    })

    // Find first incomplete field
    const sortedFields = this.getSortedFields()
    const firstIncomplete = sortedFields.findIndex(f => !this.completedFields.has(f.id))
    this.currentFieldIndex = firstIncomplete >= 0 ? firstIncomplete : 0

    this.updateProgress()
    this.updateButtonStates()
    this.updateDocumentIndicator()
  }

  handlePageRendered(event) {
    const { page, width, height } = event.detail
    this.currentPage = page
    this.canvasWidth = width
    this.canvasHeight = height
    this.renderFields()
    this.scrollToActiveField()
  }

  getSortedFields() {
    // Sort fields by document position, then page, then y, then x for logical flow
    return [...this.fieldsValue].sort((a, b) => {
      const aDocPos = a.document_position || 1
      const bDocPos = b.document_position || 1
      if (aDocPos !== bDocPos) return aDocPos - bDocPos
      if (a.page !== b.page) return a.page - b.page
      if (a.y !== b.y) return a.y - b.y
      return a.x - b.x
    })
  }

  get currentDocument() {
    if (!this.documentsValue || this.documentsValue.length === 0) return null
    return this.documentsValue[this.currentDocumentIndex]
  }

  renderFields() {
    if (!this.hasFieldOverlayTarget) return

    const overlay = this.fieldOverlayTarget
    overlay.innerHTML = ""

    // Filter fields for current page and document
    const currentDocId = this.currentDocument?.id
    const pageFields = this.fieldsValue.filter(f => {
      if (f.page !== this.currentPage) return false
      if (currentDocId && f.sign_request_document_id) {
        return f.sign_request_document_id === currentDocId
      }
      return true
    })
    const sortedFields = this.getSortedFields()
    const currentField = sortedFields[this.currentFieldIndex]

    pageFields.forEach(field => {
      const fieldEl = this.createFieldElement(field, currentField?.id === field.id)
      overlay.appendChild(fieldEl)
    })
  }

  createFieldElement(field, isActive) {
    const el = document.createElement("div")
    const isCompleted = this.completedFields.has(field.id)

    // Position using percentage-based coordinates
    el.style.position = "absolute"
    el.style.left = `${field.x * 100}%`
    el.style.top = `${field.y * 100}%`
    el.style.width = `${field.w * 100}%`
    el.style.height = `${field.h * 100}%`
    el.style.borderRadius = "4px"
    el.style.display = "flex"
    el.style.alignItems = "center"
    el.style.justifyContent = "center"
    el.style.overflow = "hidden"
    el.style.pointerEvents = "auto"
    el.style.cursor = "pointer"
    el.dataset.fieldId = field.id
    el.dataset.action = "click->signing-ceremony#clickField"

    if (isCompleted) {
      // Completed state - show value
      el.style.backgroundColor = this.hexToRgba(this.signerColorValue, 0.1)
      el.style.border = `2px solid ${this.signerColorValue}`

      const completedData = this.completedFields.get(field.id)
      const valueEl = document.createElement("span")
      valueEl.style.color = "#1a1a2e"
      valueEl.style.whiteSpace = "nowrap"

      if (field.field_type === "date") {
        valueEl.style.fontSize = this.calculateFontSize(field)
        valueEl.textContent = this.formatDate(completedData.value_text)
      } else {
        // For signatures/initials, use scaling to fit text in box
        valueEl.textContent = completedData.value_text
        if (completedData.font_class) {
          valueEl.classList.add(completedData.font_class)
        }
        // Start with a base font size, will be scaled down via transform
        valueEl.style.fontSize = "24px"
        valueEl.style.transformOrigin = "center center"

        // Calculate scale after element is added to DOM
        requestAnimationFrame(() => {
          this.scaleTextToFit(valueEl, el, field)
        })
      }

      el.appendChild(valueEl)
    } else if (isActive) {
      // Active state - highlighted
      el.style.backgroundColor = this.hexToRgba(this.signerColorValue, 0.3)
      el.style.border = `3px solid ${this.signerColorValue}`
      el.style.boxShadow = `0 0 8px ${this.hexToRgba(this.signerColorValue, 0.5)}`

      const label = document.createElement("span")
      label.style.fontSize = "11px"
      label.style.fontWeight = "600"
      label.style.color = this.signerColorValue
      label.style.textTransform = "uppercase"
      label.style.letterSpacing = "0.5px"
      label.textContent = this.getFieldLabel(field.field_type)
      el.appendChild(label)
    } else {
      // Pending state - dashed border
      el.style.backgroundColor = this.hexToRgba(this.signerColorValue, 0.1)
      el.style.border = `2px dashed ${this.signerColorValue}`
      el.style.opacity = "0.7"

      const label = document.createElement("span")
      label.style.fontSize = "10px"
      label.style.fontWeight = "500"
      label.style.color = this.signerColorValue
      label.style.textTransform = "uppercase"
      label.style.letterSpacing = "0.5px"
      label.textContent = this.getFieldLabel(field.field_type)
      el.appendChild(label)
    }

    return el
  }

  calculateFontSize(field) {
    // Calculate font size based on field height (target ~70% of field height)
    const heightPx = field.h * (this.canvasHeight || 800)
    return `${Math.max(10, Math.min(18, heightPx * 0.6))}px`
  }

  scaleTextToFit(textEl, containerEl, field) {
    // Get the actual dimensions
    const containerWidth = containerEl.offsetWidth - 8 // padding
    const containerHeight = containerEl.offsetHeight - 4

    // Measure the text at current size
    const textWidth = textEl.scrollWidth
    const textHeight = textEl.scrollHeight

    if (textWidth === 0 || textHeight === 0) return

    // Calculate scale factors
    const scaleX = containerWidth / textWidth
    const scaleY = containerHeight / textHeight

    // Use the smaller scale to fit both dimensions, cap at 1 (don't scale up)
    const scale = Math.min(scaleX, scaleY, 1)

    if (scale < 1) {
      textEl.style.transform = `scale(${scale})`
    }
  }

  formatDate(isoDate) {
    if (!isoDate) return ""
    try {
      const date = new Date(isoDate + "T00:00:00")
      const day = String(date.getDate()).padStart(2, "0")
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const year = date.getFullYear()
      return `${day}-${month}-${year}`
    } catch {
      return isoDate
    }
  }

  getFieldLabel(fieldType) {
    const labels = {
      signature: "Sign",
      initials: "Initials",
      date: "Date"
    }
    return labels[fieldType] || fieldType
  }

  hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // Navigation
  previousField() {
    if (this.currentFieldIndex > 0) {
      this.currentFieldIndex--
      this.focusCurrentField()
    }
  }

  nextField() {
    const sortedFields = this.getSortedFields()
    if (this.currentFieldIndex < sortedFields.length - 1) {
      this.currentFieldIndex++
      this.focusCurrentField()
    }
  }

  focusCurrentField() {
    const sortedFields = this.getSortedFields()
    const field = sortedFields[this.currentFieldIndex]
    if (!field) return

    const pdfController = this.application.getControllerForElementAndIdentifier(
      this.element,
      "pdf-viewer"
    )

    // Check if we need to switch documents (multi-doc)
    if (this.documentsValue && this.documentsValue.length > 1 && field.sign_request_document_id) {
      const docIndex = this.documentsValue.findIndex(d => d.id === field.sign_request_document_id)
      if (docIndex >= 0 && docIndex !== this.currentDocumentIndex) {
        this.currentDocumentIndex = docIndex
        const doc = this.documentsValue[docIndex]
        if (pdfController && doc) {
          // switchDocument reloads and goes to page 1, then we navigate to field page
          pdfController.switchDocument(doc.url).then(() => {
            if (field.page !== 1) {
              pdfController.goToPage(field.page)
            }
            this.scrollToActiveField()
          })
          this.updateDocumentIndicator()
          this.updateProgress()
          this.updateButtonStates()
          return
        }
      }
    }

    // Navigate PDF to field's page if needed
    if (pdfController && field.page !== this.currentPage) {
      pdfController.goToPage(field.page)
    } else {
      this.renderFields()
    }

    this.scrollToActiveField()
    this.updateDocumentIndicator()
    this.updateProgress()
    this.updateButtonStates()
  }

  scrollToActiveField() {
    // After fields render, scroll the active field into view within the PDF container
    requestAnimationFrame(() => {
      if (!this.hasFieldOverlayTarget) return
      const activeField = this.fieldOverlayTarget.querySelector("[data-field-id]")
      if (!activeField) return

      // Find the scrollable PDF container
      const container = this.hasPdfContainerTarget ? this.pdfContainerTarget : null
      if (container) {
        const fieldRect = activeField.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()

        // Scroll vertically if the field is outside the visible area
        if (fieldRect.top < containerRect.top || fieldRect.bottom > containerRect.bottom) {
          activeField.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }
    })
  }

  updateDocumentIndicator() {
    if (!this.documentsValue || this.documentsValue.length <= 1) return

    // Update top bar document name
    if (this.hasDocumentIndicatorTarget) {
      const doc = this.documentsValue[this.currentDocumentIndex]
      if (doc) {
        this.documentIndicatorTarget.textContent = doc.filename
      }
    }

    // Highlight active document button in sidebar
    this.documentSectionTargets.forEach(section => {
      const btn = section.querySelector("button[data-document-index]")
      if (!btn) return
      const index = parseInt(btn.dataset.documentIndex)
      if (index === this.currentDocumentIndex) {
        btn.classList.remove("btn-outline-secondary")
        btn.classList.add("btn-primary")
      } else {
        btn.classList.remove("btn-primary")
        btn.classList.add("btn-outline-secondary")
      }
    })

    // Update document badges
    this.updateDocumentBadges()
  }

  updateDocumentBadges() {
    this.documentBadgeTargets.forEach(badge => {
      const docId = badge.dataset.documentId
      const docFields = this.fieldsValue.filter(f => f.sign_request_document_id === docId)
      const completed = docFields.filter(f => this.completedFields.has(f.id)).length
      badge.textContent = `${completed}/${docFields.length}`
    })
  }

  switchToDocumentFromSidebar(event) {
    const index = parseInt(event.currentTarget.dataset.documentIndex)
    this.switchToDocument(index)
  }

  switchToDocument(index) {
    if (index === this.currentDocumentIndex) return
    const doc = this.documentsValue[index]
    if (!doc) return

    this.currentDocumentIndex = index
    const pdfController = this.application.getControllerForElementAndIdentifier(
      this.element,
      "pdf-viewer"
    )
    if (pdfController) {
      pdfController.switchDocument(doc.url)
    }
    this.updateDocumentIndicator()
  }

  clickFieldListItem(event) {
    const fieldId = event.currentTarget.dataset.fieldId
    const sortedFields = this.getSortedFields()
    const index = sortedFields.findIndex(f => f.id === fieldId)
    if (index >= 0) {
      this.currentFieldIndex = index
      this.focusCurrentField()
    }
  }

  clickField(event) {
    const fieldId = event.currentTarget.dataset.fieldId
    const sortedFields = this.getSortedFields()
    const index = sortedFields.findIndex(f => f.id === fieldId)

    if (index >= 0) {
      this.currentFieldIndex = index
      const field = sortedFields[index]

      // If not completed, open input modal
      if (!this.completedFields.has(field.id)) {
        this.openFieldInput(field)
      }

      this.updateProgress()
      this.updateButtonStates()
      this.renderFields()
    }
  }

  openFieldInput(field) {
    this.currentFieldId = field.id

    switch (field.field_type) {
      case "signature":
        this.openSignatureModal()
        break
      case "initials":
        this.openInitialsModal()
        break
      case "date":
        this.completeDateField()
        break
    }
  }

  // Signature Modal
  openSignatureModal() {
    if (this.hasSignatureInputTarget) {
      this.signatureInputTarget.value = this.signerNameValue
    }
    this.selectedSignatureFont = "rock_salt"
    this.updateSignaturePreview()
    this.updateFontSelection(this.fontOptionsTargets, "rock_salt")
    this.signatureModalInstance?.show()
  }

  updateSignaturePreview() {
    if (this.hasSignaturePreviewTarget && this.hasSignatureInputTarget) {
      const text = this.signatureInputTarget.value || this.signerNameValue
      this.signaturePreviewTarget.textContent = text

      // Update font class
      this.signaturePreviewTarget.className = "signature-text signature-preview"
      const fontClass = this.signatureFontsValue[this.selectedSignatureFont]?.class
      if (fontClass) {
        this.signaturePreviewTarget.classList.add(fontClass)
      }
    }
  }

  selectFont(event) {
    const fontId = event.currentTarget.dataset.fontId
    this.selectedSignatureFont = fontId
    this.updateFontSelection(this.fontOptionsTarget.querySelectorAll(".font-option"), fontId)
    this.updateSignaturePreview()
  }

  updateFontSelection(options, selectedFontId) {
    if (!options) return
    options.forEach(option => {
      if (option.dataset.fontId === selectedFontId) {
        option.classList.add("selected")
      } else {
        option.classList.remove("selected")
      }
    })
  }

  applySignature() {
    const value = this.signatureInputTarget?.value || this.signerNameValue
    if (!value.trim()) return

    this.completeField(this.currentFieldId, value, {
      signature_font_id: this.selectedSignatureFont
    })

    this.signatureModalInstance?.hide()
  }

  // Initials Modal
  openInitialsModal() {
    const defaultInitials = this.signerNameValue
      .split(/\s+/)
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 3)

    if (this.hasInitialsInputTarget) {
      this.initialsInputTarget.value = defaultInitials
    }
    this.selectedInitialsFont = "rock_salt"
    this.updateInitialsPreview()
    this.updateFontSelection(this.initialsFontOptionsTarget?.querySelectorAll(".font-option"), "rock_salt")
    this.initialsModalInstance?.show()
  }

  updateInitialsPreview() {
    if (this.hasInitialsPreviewTarget && this.hasInitialsInputTarget) {
      const defaultInitials = this.signerNameValue
        .split(/\s+/)
        .map(n => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 3)

      const text = this.initialsInputTarget.value || defaultInitials
      this.initialsPreviewTarget.textContent = text

      // Update font class
      this.initialsPreviewTarget.className = "signature-text signature-preview"
      const fontClass = this.signatureFontsValue[this.selectedInitialsFont]?.class
      if (fontClass) {
        this.initialsPreviewTarget.classList.add(fontClass)
      }
    }
  }

  selectInitialsFont(event) {
    const fontId = event.currentTarget.dataset.fontId
    this.selectedInitialsFont = fontId
    this.updateFontSelection(this.initialsFontOptionsTarget?.querySelectorAll(".font-option"), fontId)
    this.updateInitialsPreview()
  }

  applyInitials() {
    const defaultInitials = this.signerNameValue
      .split(/\s+/)
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 3)

    const value = this.initialsInputTarget?.value || defaultInitials
    if (!value.trim()) return

    this.completeField(this.currentFieldId, value, {
      signature_font_id: this.selectedInitialsFont
    })

    this.initialsModalInstance?.hide()
  }

  // Date field
  completeDateField() {
    const today = new Date()
    const isoDate = today.toISOString().split("T")[0] // YYYY-MM-DD
    this.completeField(this.currentFieldId, isoDate, {})
  }

  // API calls
  async completeField(fieldId, valueText, valueMeta) {
    const url = `/${this.localeValue}/sign/${this.tokenValue}/fields/${fieldId}`

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          value_text: valueText,
          value_meta: valueMeta
        })
      })

      if (!response.ok) {
        const error = await response.json()
        alert(error.error || "An error occurred")
        return
      }

      const data = await response.json()

      // Update local state
      this.completedFields.set(fieldId, {
        value_text: data.value_text,
        value_meta: data.value_meta,
        font_class: data.font_class
      })

      // Move to next field if available
      this.moveToNextIncomplete()

      // Re-render and update UI
      this.renderFields()
      this.updateProgress()
      this.updateButtonStates()
      this.updateFieldList()
    } catch (error) {
      console.error("Error completing field:", error)
      alert("An error occurred. Please try again.")
    }
  }

  moveToNextIncomplete() {
    const sortedFields = this.getSortedFields()
    const nextIncomplete = sortedFields.findIndex(
      (f, i) => i > this.currentFieldIndex && !this.completedFields.has(f.id)
    )

    if (nextIncomplete >= 0) {
      this.currentFieldIndex = nextIncomplete
      this.focusCurrentField()
    }
  }

  async submit() {
    if (this.submitting) return

    // Check if all required fields are completed
    const incompleteRequired = this.fieldsValue.filter(
      f => f.required && !this.completedFields.has(f.id)
    )

    if (incompleteRequired.length > 0) {
      // Navigate to first incomplete required field
      const sortedFields = this.getSortedFields()
      const firstIncomplete = sortedFields.findIndex(
        f => f.required && !this.completedFields.has(f.id)
      )
      if (firstIncomplete >= 0) {
        this.currentFieldIndex = firstIncomplete
        this.focusCurrentField()
      }
      return
    }

    this.submitting = true
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span> Processing...'

    const url = `/${this.localeValue}/sign/${this.tokenValue}/submit`

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        }
      })

      if (!response.ok) {
        const error = await response.json()
        alert(error.error || "An error occurred")
        this.submitting = false
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.innerHTML = '<i class="bi bi-check2-circle me-1"></i> Finish Signing'
        return
      }

      const data = await response.json()

      // Redirect to completion page
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      } else {
        window.location.reload()
      }
    } catch (error) {
      console.error("Error submitting:", error)
      alert("An error occurred. Please try again.")
      this.submitting = false
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.innerHTML = '<i class="bi bi-check2-circle me-1"></i> Finish Signing'
    }
  }

  // UI Updates
  updateProgress() {
    const totalFields = this.fieldsValue.length
    const completedCount = this.completedFields.size
    const percentage = totalFields > 0 ? (completedCount / totalFields) * 100 : 0

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${completedCount} of ${totalFields} completed`
    }
  }

  updateButtonStates() {
    const sortedFields = this.getSortedFields()

    if (this.hasPreviousBtnTarget) {
      this.previousBtnTarget.disabled = this.currentFieldIndex === 0
    }

    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.disabled = this.currentFieldIndex >= sortedFields.length - 1
    }

    if (this.hasSubmitBtnTarget) {
      const allRequiredComplete = this.fieldsValue
        .filter(f => f.required)
        .every(f => this.completedFields.has(f.id))
      this.submitBtnTarget.disabled = !allRequiredComplete || this.submitting
    }
  }

  updateFieldList() {
    if (!this.hasFieldListItemTarget) return

    this.fieldListItemTargets.forEach(item => {
      const fieldId = item.dataset.fieldId
      const isCompleted = this.completedFields.has(fieldId)

      // Update badge
      const badge = item.querySelector(".badge")
      if (badge && isCompleted) {
        badge.innerHTML = '<i class="bi bi-check"></i>'
      }

      // Update text style
      const textSpan = item.querySelector("span:not(.badge)")
      if (textSpan) {
        if (isCompleted) {
          textSpan.classList.add("text-decoration-line-through", "text-muted")
        } else {
          textSpan.classList.remove("text-decoration-line-through", "text-muted")
        }
      }

      // Add/remove check icon
      let checkIcon = item.querySelector(".bi-check-circle-fill")
      if (isCompleted && !checkIcon) {
        checkIcon = document.createElement("i")
        checkIcon.className = "bi bi-check-circle-fill text-success ms-auto"
        item.appendChild(checkIcon)
      } else if (!isCompleted && checkIcon) {
        checkIcon.remove()
      }
    })
  }
}
